/**
 * What the manual covers, in the order a person meets it.
 *
 * This file holds STRUCTURE only — which screens belong to which module, which
 * measured markers each screen explains, and where the reader can go to try it.
 * The words live in the i18n vocabs (`MANUAL.*`) so the manual translates like
 * everything else, and the marker coordinates live in the generated
 * `manual.hotspots.ts` so a re-capture moves them without a code change.
 *
 * Key convention — for a module `QUIZ` with a step `RESULT` and a marker
 * `score`, the copy is at:
 *
 *   MANUAL.M.QUIZ.TITLE / .DESC / .VIDEO
 *   MANUAL.M.QUIZ.S.RESULT.TITLE / .BODY
 *   MANUAL.M.QUIZ.S.RESULT.MARKS.score
 *
 * `marks` is deliberately an ordered list rather than whatever the manifest
 * happens to contain: it is the reading order of the numbered bullets. Markers
 * the capture could not measure (a control that only appears for some accounts)
 * are dropped at render time, and their numbering closes up.
 */
export interface ManualStepDef {
  /** Step key, used for both the i18n path and the DOM id. */
  id: string;
  /** Matches an id in the generated hotspot manifest. */
  shot: string;
  /** Marker keys, in reading order. */
  marks: string[];
}

export interface ManualModuleDef {
  id: string;
  /** Keenicon name for the module header. */
  icon: string;
  /** Where in the app this module lives, so the reader can go and try it. */
  route: string;
  /** `assets/manual/video/<video>.mp4`, built by test/tools/manual-video.mjs. */
  video: string;
  steps: ManualStepDef[];
}

export const MANUAL_MODULES: ManualModuleDef[] = [
  {
    id: "GETTING_STARTED",
    icon: "rocket",
    route: "/home",
    video: "getting-started",
    steps: [
      { id: "HOME", shot: "getting-started.home", marks: ["nav", "cta", "login"] },
      {
        id: "SIGN_IN",
        shot: "getting-started.signin",
        marks: ["email", "demo", "forgot", "register"],
      },
      { id: "REGISTER", shot: "getting-started.register", marks: ["role"] },
      {
        id: "SIGNED_IN",
        shot: "getting-started.signed-in",
        marks: ["avatar", "cart", "ribbon"],
      },
    ],
  },
  {
    id: "COURSES",
    icon: "book-open",
    route: "/courses",
    video: "courses",
    steps: [
      { id: "LIST", shot: "courses.list", marks: ["card", "filter"] },
      {
        id: "DETAIL",
        shot: "courses.detail",
        marks: ["title", "enroll", "cart", "trial"],
      },
    ],
  },
  {
    id: "PURCHASE",
    icon: "basket",
    route: "/purchase-order",
    video: "purchase",
    steps: [
      { id: "CART", shot: "purchase.cart", marks: ["items", "notice", "pay"] },
      { id: "ORDERS", shot: "purchase.orders", marks: ["table", "row"] },
      { id: "ORDER_DETAIL", shot: "purchase.order-detail", marks: ["detail", "code"] },
    ],
  },
  {
    id: "ONLINE_CLASS",
    icon: "monitor-mobile",
    route: "/online-class",
    video: "online-class",
    steps: [
      {
        id: "DASHBOARD",
        shot: "online-class.dashboard",
        marks: ["heading", "card", "enter"],
      },
      { id: "START", shot: "online-class.start-dialog", marks: ["enter", "pause"] },
      { id: "WATCH", shot: "online-class.watch", marks: ["player", "chapters", "tabs"] },
      {
        id: "LESSON_QUIZZES",
        shot: "online-class.lesson-quizzes",
        marks: ["quizzes"],
      },
    ],
  },
  {
    id: "QUIZ",
    icon: "note-2",
    route: "/online-class/quiz",
    video: "quiz",
    steps: [
      { id: "LIST", shot: "quiz.list", marks: ["list", "start", "best"] },
      { id: "DRILLS", shot: "quiz.drills", marks: ["drills"] },
      {
        id: "QUESTION",
        shot: "quiz.question",
        marks: ["index", "body", "choice", "advance"],
      },
      {
        id: "REVIEW",
        shot: "quiz.review",
        marks: ["heading", "unanswered", "submit"],
      },
      { id: "RESULT", shot: "quiz.result", marks: ["score", "difficulty"] },
      { id: "BREAKDOWN", shot: "quiz.breakdown", marks: ["skills"] },
      { id: "ANSWER_KEY", shot: "quiz.answer-key", marks: ["answers"] },
    ],
  },
  {
    id: "SKILLS",
    icon: "chart-simple",
    route: "/online-class/skills",
    video: "skills",
    steps: [
      { id: "OVERVIEW", shot: "skills.overview", marks: ["heading", "narrative"] },
      { id: "WEAK", shot: "skills.weak-topics", marks: ["topic"] },
      { id: "NEXT", shot: "skills.next-steps", marks: ["recommendations"] },
    ],
  },
  {
    id: "PROFILE",
    icon: "profile-circle",
    route: "/profile/monitor/study-record",
    video: "profile",
    steps: [
      {
        id: "STUDY_RECORD",
        shot: "profile.study-record",
        marks: ["heading", "chart", "activity", "tabs"],
      },
      { id: "QUIZ_RECORD", shot: "profile.quiz-record", marks: ["record", "tabskills"] },
      { id: "ACCOUNT", shot: "profile.account", marks: ["save"] },
      { id: "PARENT", shot: "profile.parent", marks: ["add", "code"] },
      { id: "PASSWORD", shot: "profile.password", marks: ["save"] },
    ],
  },
  {
    id: "BLOG",
    icon: "message-text-2",
    route: "/blogs",
    video: "blog",
    steps: [
      { id: "LIST", shot: "blog.list", marks: ["search", "row"] },
      { id: "DETAIL", shot: "blog.detail", marks: ["article"] },
    ],
  },
];
